import { AlertTriangle, Filter, Calendar, Users, X } from 'lucide-react'
import { cn } from '@/utils/cn'
import { PAGE_TOOLBAR } from '@/components/ui/page-toolbar'
import { stageConfig } from './deals-constants'
import type { DealCloserOption } from '@/hooks/useDealsTeamClosers'
import type { DealStage } from '@/types'

const selectClass = cn(PAGE_TOOLBAR.select, 'w-full')

const PERIOD_OPTIONS = [
  { value: 'all', label: 'Todo período' },
  { value: 'month', label: 'Este mês' },
  { value: 'quarter', label: 'Este trimestre' },
  { value: 'year', label: 'Este ano' },
] as const

export interface DealsFiltersPanelProps {
  open: boolean
  onClose: () => void
  onClearAll: () => void
  stagesForTab: DealStage[]
  etapaFilter: string
  setEtapaFilter: (v: string) => void
  periodFilter: string
  setPeriodFilter: (v: string) => void
  showStagnantOnly: boolean
  setShowStagnantOnly: (v: boolean) => void
  showCloserSelect: boolean
  closerFilter: string
  setCloserFilter: (v: string) => void
  closers: DealCloserOption[]
}

export function DealsFiltersPanel({
  open,
  onClose,
  onClearAll,
  stagesForTab,
  etapaFilter,
  setEtapaFilter,
  periodFilter,
  setPeriodFilter,
  showStagnantOnly,
  setShowStagnantOnly,
  showCloserSelect,
  closerFilter,
  setCloserFilter,
  closers,
}: DealsFiltersPanelProps) {
  if (!open) return null

  return (
    <>
      <div
        className='fixed inset-0 z-40 bg-black/40 md:hidden'
        aria-hidden
        onClick={onClose}
      />
      <div
        role='dialog'
        aria-label='Filtros avançados'
        className={cn(
          'z-50 rounded-xl border border-[var(--glass-border)] bg-[var(--bg-card)] p-4 shadow-2xl',
          'fixed inset-x-4 bottom-4 max-h-[70vh] overflow-y-auto md:absolute md:inset-auto md:right-0 md:top-full md:mt-2 md:w-80',
        )}
      >
        <div className='mb-4 flex items-center justify-between'>
          <h3 className='text-sm font-semibold text-text-primary'>Filtros avançados</h3>
          <button
            type='button'
            onClick={onClose}
            className='flex h-8 w-8 items-center justify-center rounded-lg text-text-muted hover:bg-white/[0.06] hover:text-text-primary'
            aria-label='Fechar filtros'
          >
            <X size={16} />
          </button>
        </div>

        <div className='flex flex-col gap-4'>
          {stagesForTab.length > 1 && (
            <label className='flex flex-col gap-1.5'>
              <span className='flex items-center gap-1.5 text-xs font-medium text-text-muted'>
                <Filter size={13} aria-hidden />
                Etapa
              </span>
              <select
                className={selectClass}
                value={etapaFilter}
                onChange={(e) => setEtapaFilter(e.target.value)}
              >
                <option value='all'>Todas as etapas</option>
                {stagesForTab.map((stage) => (
                  <option key={stage} value={stage}>
                    {stageConfig[stage]?.label ?? stage}
                  </option>
                ))}
              </select>
            </label>
          )}

          <label className='flex flex-col gap-1.5'>
            <span className='flex items-center gap-1.5 text-xs font-medium text-text-muted'>
              <Calendar size={13} aria-hidden />
              Período
            </span>
            <select
              className={selectClass}
              value={periodFilter}
              onChange={(e) => setPeriodFilter(e.target.value)}
            >
              {PERIOD_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </label>

          <label className='flex cursor-pointer items-center justify-between gap-3 rounded-lg border border-[var(--glass-border)] bg-[var(--glass-bg)] px-3 py-3'>
            <span className='flex items-center gap-2 text-sm text-text-primary'>
              <AlertTriangle size={15} className='text-amber-400' aria-hidden />
              Apenas parados
            </span>
            <input
              type='checkbox'
              checked={showStagnantOnly}
              onChange={(e) => setShowStagnantOnly(e.target.checked)}
              className='h-4 w-4 rounded border-[var(--glass-border)] accent-[var(--accent)]'
            />
          </label>

          {showCloserSelect && (
            <label className='flex flex-col gap-1.5'>
              <span className='flex items-center gap-1.5 text-xs font-medium text-text-muted'>
                <Users size={13} aria-hidden />
                Closer
              </span>
              <select
                className={selectClass}
                value={closerFilter}
                onChange={(e) => setCloserFilter(e.target.value)}
              >
                <option value='all'>Todos os Closers</option>
                {closers.map((closer) => (
                  <option key={closer.id} value={closer.id}>
                    {closer.name}
                  </option>
                ))}
              </select>
            </label>
          )}
        </div>

        <div className='mt-4 flex gap-2 border-t border-[var(--glass-border)] pt-4'>
          <button
            type='button'
            onClick={onClearAll}
            className='flex-1 rounded-lg border border-[var(--glass-border)] bg-[var(--glass-bg)] px-3 py-2 text-sm font-medium text-text-muted transition-all hover:bg-white/[0.06] hover:text-text-primary'
          >
            Limpar tudo
          </button>
          <button
            type='button'
            onClick={onClose}
            className='flex-1 rounded-lg bg-accent px-3 py-2 text-sm font-medium text-white transition-all hover:opacity-90'
          >
            Aplicar
          </button>
        </div>
      </div>
    </>
  )
}
